import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { ApiError } from '../../api/client'
import {
  customKitFormQuery,
  customPackageFormQuery,
  kitFormQuery,
  osLabel,
  saveCustomKit,
  saveCustomPackage,
  saveKit,
} from '../../api/settingsSoftsee'
import { Modal } from '../../ui/Modal'
import { newAlert, toast } from '../../ui/notify'
import { SelectPicker } from '../../ui/SelectPicker'
import { Spinner } from '../../ui/Spinner'
import './SoftseeKitEditModals.css'

/**
 * Settings > Softsee: ferestrele care editeaza un kit, un kit propriu si un
 * pachet - editKitForm.php, editCustomKitForm.php si editCustomPackageForm.php
 * de pe cec-master, cu perechile lor saveKit / saveCustomKit /
 * saveCustomPackage. Pana pe 18 septembrie 2026 ecranele v4 stiau doar sa
 * adauge si sa stearga: un kit gresit se stergea si se facea din nou.
 *
 * Fisierul MSI nu se schimba de aici. Numele lui se vede, dar butonul
 * "Change MSI" din vechi urca fisierul pe serverul de download
 * (`changeMSI`), iar v4 n-are inca drumul ala - un buton care ar salva doar
 * numele ar lasa statiile sa ceara un fisier care nu exista.
 */

function num(row: Record<string, unknown>, key: string): number {
  return Number(row[key] ?? 0)
}

function str(row: Record<string, unknown>, key: string): string {
  return String(row[key] ?? '')
}

function ids(values: number[] | undefined): string[] {
  return (values ?? []).map((v) => String(v))
}

/** Randul de formular al zonei: eticheta de 3 coloane, campul de 9. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="form-group">
      <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
        <span className="color">{label}</span>
      </label>
      <div className="col-sm-9">{children}</div>
    </div>
  )
}

function MsiName({ name }: { name: string }) {
  return (
    <Row label="MSI">
      <p className="form-control-static msi-name">{name || <i className="fas fa-minus font-10"></i>}</p>
    </Row>
  )
}

function Forbidden() {
  return <div className="alert alert-danger">You do not have access to this action.</div>
}

function text(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name)
  return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement ? field.value : ''
}

function Footer({ form, onClose, ready }: { form: string; onClose(): void; ready: boolean }) {
  return (
    <>
      <button type="button" className="btn btn-default" onClick={onClose}>
        Close
      </button>
      <button type="submit" form={form} className="btn btn-primary" disabled={!ready}>
        <i className="far fa-save"></i> Save
      </button>
    </>
  )
}

/**
 * Sertarele se seamana O SINGURA data, cand soseste formularul: de atunci
 * sunt ale omului, si o redesenare nu-i pune la loc ce scria in baza.
 */
function useSeed(ready: boolean, seed: () => void) {
  const seeded = useRef(false)
  const latest = useRef(seed)
  latest.current = seed
  useEffect(() => {
    if (!ready || seeded.current) return
    seeded.current = true
    latest.current()
  }, [ready])
}

async function submit(save: () => Promise<unknown>, message: string, done: () => Promise<void>) {
  try {
    await save()
    toast(message, '', 'success')
    await done()
  } catch (err) {
    newAlert(err instanceof Error ? err.message : 'Error', 'danger')
  }
}

export function EditKitModal({ id, onClose }: { id: number; onClose(): void }) {
  const query = useQueryClient()
  const page = useQuery(kitFormQuery(id))
  const data = page.data
  const [category, setCategory] = useState('')
  const [pack, setPack] = useState('')
  const [os, setOs] = useState<string[]>([])
  useSeed(!!data, () => {
    if (!data) return
    setCategory(String(num(data.kit, 'category_id') || ''))
    setPack(String(num(data.kit, 'pack_id') || ''))
    setOs(ids(data.kitOs))
  })
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget
    void submit(
      () =>
        saveKit(id, {
          kit_name: text(form, 'kit_name'),
          kit_version: text(form, 'kit_version'),
          kit_category: category,
          kit_pack: pack,
          kit_exe: text(form, 'kit_exe'),
          kit_params: text(form, 'kit_params'),
          kit_os: os,
        }),
      'Kit saved.',
      async () => {
        onClose()
        await query.invalidateQueries({ queryKey: ['settings', 'softsee', 'kits'] })
      },
    )
  }
  return (
    <Modal id="editKitModal" open={true} title="Kit details" onClose={onClose} footer={<Footer form="editKitForm" onClose={onClose} ready={!!data} />}>
      {page.error instanceof ApiError && page.error.status === 403 ? (
        <Forbidden />
      ) : page.error ? (
        <div className="alert alert-danger">Something went wrong: {page.error.message}</div>
      ) : !data ? (
        <Spinner />
      ) : (
        <form className="form-horizontal labelcustomize" id="editKitForm" data-collabel="3" data-alignlabel="left" data-label="color" onSubmit={onSubmit}>
          <MsiName name={str(data.kit, 'msi_name')} />
          <Row label="Software name">
            <input className="form-control" name="kit_name" type="text" defaultValue={str(data.kit, 'name')} required />
          </Row>
          <Row label="Software version">
            <input className="form-control" name="kit_version" type="text" defaultValue={str(data.kit, 'version')} required />
          </Row>
          <Row label="Category">
            <SelectPicker
              id="editKitCategory"
              name="kit_category"
              className="form-control"
              title="Select.."
              value={category}
              onChange={setCategory}
              options={data.app_categories.map((c) => ({ value: String(num(c, 'ID')), label: str(c, 'category_name') }))}
            />
          </Row>
          <Row label="Pack">
            <SelectPicker
              id="editKitPack"
              name="kit_pack"
              className="form-control"
              title="Select.."
              value={pack}
              onChange={setPack}
              options={data.packs.map((p) => ({ value: String(num(p, 'ID')), label: str(p, 'pack_name') }))}
            />
          </Row>
          <Row label="Exe">
            <input className="form-control" name="kit_exe" type="text" defaultValue={str(data.kit, 'exe')} />
            <span className="help-block">
              <i className="fas fa-info-circle"></i> Executable to close when the script is ran
            </span>
          </Row>
          <Row label="Params">
            <textarea style={{ maxWidth: '100%' }} className="form-control" name="kit_params" defaultValue={str(data.kit, 'parameters')}></textarea>
          </Row>
          <Row label="Operating system">
            <SelectPicker
              id="editKitOs"
              name="kit_os[]"
              className="form-control"
              title="Select.."
              multiple
              selectedTextFormat="count > 2"
              size={10}
              value={os}
              onChange={setOs}
              options={data.operating_systems.map((row) => ({ value: String(num(row, 'ID')), label: osLabel(row) }))}
            />
          </Row>
        </form>
      )}
    </Modal>
  )
}

export function EditCustomKitModal({ id, onClose }: { id: number; onClose(): void }) {
  const query = useQueryClient()
  const page = useQuery(customKitFormQuery(id))
  const data = page.data
  const [company, setCompany] = useState('')
  const [category, setCategory] = useState('')
  const [os, setOs] = useState<string[]>([])
  const [packages, setPackages] = useState<string[]>([])
  useSeed(!!data, () => {
    if (!data) return
    setCompany(String(num(data.customKit, 'CompanyID') || ''))
    setCategory(String(num(data.customKit, 'CategoryID') || ''))
    setOs(ids(data.kitOS))
    setPackages(ids(data.kitPackages))
  })
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget
    void submit(
      () =>
        saveCustomKit(id, {
          name: text(form, 'name'),
          version: text(form, 'version'),
          company,
          category,
          exe_to_kill: text(form, 'exe_to_kill'),
          parameters: text(form, 'parameters'),
          os,
          packages,
        }),
      'Kit saved.',
      async () => {
        onClose()
        await query.invalidateQueries({ queryKey: ['settings', 'softsee', 'custom-kits'] })
      },
    )
  }
  return (
    <Modal
      id="editCustomKitModal"
      open={true}
      title="Custom kit details"
      onClose={onClose}
      footer={<Footer form="editCustomKitForm" onClose={onClose} ready={!!data} />}
    >
      {page.error instanceof ApiError && page.error.status === 403 ? (
        <Forbidden />
      ) : page.error ? (
        <div className="alert alert-danger">Something went wrong: {page.error.message}</div>
      ) : !data ? (
        <Spinner />
      ) : (
        <form className="form-horizontal labelcustomize" id="editCustomKitForm" data-collabel="3" data-alignlabel="left" data-label="color" onSubmit={onSubmit}>
          <MsiName name={str(data.customKit, 'MsiName')} />
          <Row label="Software name">
            <input className="form-control" name="name" type="text" defaultValue={str(data.customKit, 'Name')} required />
          </Row>
          <Row label="Software version">
            <input className="form-control" name="version" type="text" defaultValue={str(data.customKit, 'Version')} required />
          </Row>
          <Row label="Company">
            <SelectPicker
              id="editCustomKitCompany"
              name="company"
              className="form-control"
              title="Select.."
              value={company}
              onChange={setCompany}
              options={data.companies.map((c) => ({ value: String(num(c, 'ID')), label: str(c, 'Nume') }))}
            />
          </Row>
          <Row label="Category">
            <SelectPicker
              id="editCustomKitCategory"
              name="category"
              className="form-control"
              title="Select.."
              value={category}
              onChange={setCategory}
              options={data.categories.map((c) => ({ value: String(num(c, 'ID')), label: str(c, 'category_name') }))}
            />
          </Row>
          <Row label="Exe">
            <input className="form-control" name="exe_to_kill" type="text" defaultValue={str(data.customKit, 'KillService')} />
            <span className="help-block">
              <i className="fas fa-info-circle"></i> Executable to close when the script is ran
            </span>
          </Row>
          <Row label="Params">
            <textarea style={{ maxWidth: '100%' }} className="form-control" name="parameters" defaultValue={str(data.customKit, 'Parameters')}></textarea>
          </Row>
          <Row label="Operating system">
            <SelectPicker
              id="editCustomKitOs"
              name="os[]"
              className="form-control"
              title="Select.."
              multiple
              selectedTextFormat="count > 2"
              size={10}
              value={os}
              onChange={setOs}
              options={data.operating_systems.map((row) => ({ value: String(num(row, 'ID')), label: osLabel(row) }))}
            />
          </Row>
          <Row label="Add to Package">
            <SelectPicker
              id="editCustomKitPackages"
              name="packages[]"
              className="form-control"
              title="Select.."
              multiple
              selectedTextFormat="count > 2"
              size={10}
              value={packages}
              onChange={setPackages}
              options={data.packages.map((p) => ({ value: String(num(p, 'ID')), label: str(p, 'Name') }))}
            />
          </Row>
        </form>
      )}
    </Modal>
  )
}

export function EditCustomPackageModal({ id, onClose }: { id: number; onClose(): void }) {
  const query = useQueryClient()
  const page = useQuery(customPackageFormQuery(id))
  const data = page.data
  const [company, setCompany] = useState('')
  const [customKits, setCustomKits] = useState<string[]>([])
  const [standardKits, setStandardKits] = useState<string[]>([])
  useSeed(!!data, () => {
    if (!data) return
    setCompany(String(num(data.customPackage, 'CompanyID') || ''))
    setCustomKits(ids(data.package_custom_kits))
    setStandardKits(ids(data.package_main_kits))
  })
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget
    void submit(
      () =>
        saveCustomPackage(id, {
          name: text(form, 'name'),
          description: text(form, 'description'),
          company,
          custom_kits: customKits,
          standard_kits: standardKits,
        }),
      'Package saved.',
      async () => {
        onClose()
        await query.invalidateQueries({ queryKey: ['settings', 'softsee', 'custom-packages'] })
      },
    )
  }
  return (
    <Modal
      id="editCustomPackageModal"
      open={true}
      title="Package details"
      onClose={onClose}
      footer={<Footer form="editCustomPackageForm" onClose={onClose} ready={!!data} />}
    >
      {page.error instanceof ApiError && page.error.status === 403 ? (
        <Forbidden />
      ) : page.error ? (
        <div className="alert alert-danger">Something went wrong: {page.error.message}</div>
      ) : !data ? (
        <Spinner />
      ) : (
        <form className="form-horizontal labelcustomize" id="editCustomPackageForm" data-collabel="3" data-alignlabel="left" data-label="color" onSubmit={onSubmit}>
          <Row label="Package name">
            <input className="form-control" name="name" type="text" defaultValue={str(data.customPackage, 'Name')} required />
          </Row>
          <Row label="Description">
            <input className="form-control" name="description" type="text" defaultValue={str(data.customPackage, 'Description')} />
          </Row>
          <Row label="Company">
            <SelectPicker
              id="editCustomPackageCompany"
              name="company"
              className="form-control"
              title="Select.."
              value={company}
              onChange={setCompany}
              options={data.companies.map((c) => ({ value: String(num(c, 'ID')), label: str(c, 'Nume') }))}
            />
          </Row>
          {/* Un sertar lasat gol pastreaza kiturile de felul lui (savingCustomPackage). */}
          <Row label="Custom Kits">
            <SelectPicker
              id="editCustomPackageCustomKits"
              name="custom_kits[]"
              className="form-control"
              title="Select.."
              multiple
              selectedTextFormat="count > 2"
              size={10}
              value={customKits}
              onChange={setCustomKits}
              options={data.custom_kits.map((k) => ({ value: String(num(k, 'ID')), label: str(k, 'Name') }))}
            />
          </Row>
          <Row label="Standard Kits">
            <SelectPicker
              id="editCustomPackageStandardKits"
              name="standard_kits[]"
              className="form-control"
              title="Select.."
              multiple
              selectedTextFormat="count > 2"
              size={10}
              value={standardKits}
              onChange={setStandardKits}
              options={data.main_kits.map((k) => ({ value: String(num(k, 'ID')), label: str(k, 'name') }))}
            />
          </Row>
        </form>
      )}
    </Modal>
  )
}
